import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';

import { Button } from '../../components/ui/Button.js';
import { ConfirmModal } from '../../components/ui/ConfirmModal.js';
import { Table, type TableColumn } from '../../components/ui/Table.js';
import { ToastStack } from '../../components/ui/Toast.js';
import { useApi } from '../../hooks/useApi.js';
import { useAuth } from '../../hooks/useAuth.js';
import { useToasts } from '../../hooks/useToasts.js';
import { ApiError } from '../../lib/api.js';
import type { Usuario } from '../clientes/types.js';
// Prompt BJ-04 — ROL_LABEL/formatUsuarioLabel centralizados en @leadsphere/types
// (antes duplicado inline aquí).
import { formatUsuarioLabel, ROL_LABEL } from '@leadsphere/types';

function fmtUltimoAcceso(iso: string | null): string {
  if (!iso) return 'Nunca';
  const diffMs = Date.now() - new Date(iso).getTime();
  const diffMin = Math.floor(diffMs / 60_000);
  if (diffMin < 1) return 'Hace un momento';
  if (diffMin < 60) return `Hace ${diffMin} min`;
  const diffHoras = Math.floor(diffMin / 60);
  if (diffHoras < 24) return `Hace ${diffHoras} hora${diffHoras !== 1 ? 's' : ''}`;
  const diffDias = Math.floor(diffHoras / 24);
  return `Hace ${diffDias} día${diffDias !== 1 ? 's' : ''}`;
}

// Prompt BB-3(RH) — Pantalla 1: mismo listado (Nombre/Email/Rol/Área/Activo/
// Último acceso) que ya existía, pero cada fila ahora navega a
// UsuarioDetalle.tsx (ver/editar completo) en vez de abrir un modal de
// edición inline — mismo patrón lista+detalle ya usado en el resto del
// proyecto (Clientes, Tickets, Días Festivos, Tipos de Ausencia).
export function UsuariosPage() {
  const { get, patch, delete: del } = useApi();
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toasts, showToast, dismiss } = useToasts();

  // Prompt AF-4(RH) — 'CEO' ve esta pantalla (lectura equivalente a root),
  // pero canManageUsers=false para CEO (igual que kam) — el backend ya
  // rechaza POST/PATCH/DELETE con 403 para cualquiera que no sea root; este
  // flag solo evita mostrar controles de escritura que fallarían en la UI.
  const puedeEscribir = user?.rol === 'root';

  const [search, setSearch] = useState('');
  const [rolFiltro, setRolFiltro] = useState('');
  const [desactivarPendiente, setDesactivarPendiente] = useState<Usuario | null>(null);
  const [eliminarPendiente, setEliminarPendiente] = useState<Usuario | null>(null);

  // Mismo query key que ClientesPage.tsx/NuevoCliente.tsx (lookup de KAM
  // Asignado) — GET /usuarios no distingue "admin" de "lookup", así que
  // comparten caché y se refrescan mutuamente al invalidar. Usuarios
  // eliminados (soft-delete) ya vienen excluidos desde el backend.
  const { data: usuarios, isLoading } = useQuery({
    queryKey: ['usuarios'],
    queryFn: () => get<Usuario[]>('/usuarios'),
  });

  const usuariosFiltrados = useMemo(() => {
    const s = search.trim().toLowerCase();
    return (usuarios ?? []).filter(u => {
      if (s && !u.nombre.toLowerCase().includes(s) && !u.correo.toLowerCase().includes(s)) return false;
      if (rolFiltro && u.rol !== rolFiltro) return false;
      return true;
    });
  }, [usuarios, search, rolFiltro]);

  function toggleFiltroRol(rol: string) {
    setRolFiltro(prev => (prev === rol ? '' : rol));
  }

  const toggleMutation = useMutation({
    mutationFn: ({ id, activo }: { id: string; activo: boolean }) => patch(`/usuarios/${id}/activo`, { activo }),
    onSuccess: (_data, { activo }) => {
      queryClient.invalidateQueries({ queryKey: ['usuarios'] });
      showToast('success', activo ? 'Usuario reactivado correctamente.' : 'Usuario desactivado — su sesión activa fue cerrada de inmediato.');
    },
    onError: (err: unknown) => {
      showToast('error', err instanceof ApiError ? err.message : 'No fue posible actualizar el estatus del usuario.');
    },
  });

  const eliminarMutation = useMutation({
    mutationFn: (id: string) => del(`/usuarios/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['usuarios'] });
      showToast('success', 'Usuario eliminado. Sus registros históricos se conservan intactos.');
    },
    onError: (err: unknown) => {
      showToast('error', err instanceof ApiError ? err.message : 'No fue posible eliminar el usuario.');
    },
  });

  function handleToggle(u: Usuario, activo: boolean) {
    if (!activo) {
      setDesactivarPendiente(u);
      return;
    }
    toggleMutation.mutate({ id: u.id, activo: true });
  }

  function confirmarDesactivar() {
    if (desactivarPendiente) toggleMutation.mutate({ id: desactivarPendiente.id, activo: false });
    setDesactivarPendiente(null);
  }

  function confirmarEliminar() {
    if (eliminarPendiente) eliminarMutation.mutate(eliminarPendiente.id);
    setEliminarPendiente(null);
  }

  const columns: TableColumn<Usuario>[] = [
    { key: 'nombre', header: 'Nombre', render: u => <span style={{ fontWeight: 600 }}>{u.nombre}</span> },
    { key: 'correo', header: 'Email', render: u => <span style={{ color: 'var(--muted)' }}>{u.correo}</span> },
    {
      key: 'rol',
      header: 'Rol',
      render: u => (
        <button
          className={`role-pill role-pill-${u.rol}`}
          title={`Filtrar por rol "${ROL_LABEL[u.rol] ?? u.rol}"`}
          onClick={e => {
            e.stopPropagation();
            toggleFiltroRol(u.rol);
          }}
        >
          {ROL_LABEL[u.rol] ?? u.rol}
        </button>
      ),
    },
    {
      key: 'puesto',
      header: 'Puesto',
      // Prompt BJ-04 — formatUsuarioLabel(): puesto con asterisco si es_interno,
      // fallback al label del rol si no hay puesto capturado.
      render: u => <span style={{ color: 'var(--muted)' }}>{formatUsuarioLabel(u)}</span>,
    },
    { key: 'area', header: 'Área', render: u => <span style={{ color: 'var(--muted)' }}>{u.area ?? '—'}</span> },
    {
      key: 'activo',
      header: 'Activo',
      align: 'center',
      render: u => {
        // El backend rechaza pausar/eliminar CUALQUIER usuario con rol root
        // (no solo "a sí mismo") — ver usuarios.service.ts. Se bloquea aquí
        // por rol (superset de "no te desactives a ti mismo") para que el
        // candado en UI refleje la regla real del backend.
        const esRoot = u.rol === 'root' || u.rol === 'CEO';
        const esUnoMismo = u.id === user?.sub;
        const bloqueado = esRoot || !puedeEscribir;
        const title = !puedeEscribir
          ? 'No tienes permiso para modificar usuarios.'
          : esRoot
            ? esUnoMismo
              ? 'No puedes desactivar tu propia cuenta.'
              : 'Este usuario no se puede desactivar.'
            : u.activo
              ? 'Desactivar usuario'
              : 'Activar usuario';
        return (
          <div onClick={e => e.stopPropagation()} style={{ display: 'flex', justifyContent: 'center' }}>
            <label className="tog" title={title}>
              <input
                type="checkbox"
                checked={u.activo}
                disabled={bloqueado}
                onChange={e => handleToggle(u, e.target.checked)}
              />
              <span className="tog-slider" />
            </label>
          </div>
        );
      },
    },
    { key: 'ultimo_login_at', header: 'Último acceso', render: u => <span style={{ color: 'var(--muted)' }}>{fmtUltimoAcceso(u.ultimo_login_at)}</span> },
    {
      key: 'acciones',
      header: '',
      render: u => {
        if (!puedeEscribir) return null;
        const esRoot = u.rol === 'root' || u.rol === 'CEO';
        const esUnoMismo = u.id === user?.sub;
        return (
          <div onClick={e => e.stopPropagation()} style={{ display: 'flex', gap: 2 }}>
            <button
              className="cat-action-btn del"
              title={
                esRoot
                  ? esUnoMismo
                    ? 'No puedes eliminar tu propia cuenta.'
                    : 'Este usuario no se puede eliminar.'
                  : 'Eliminar usuario'
              }
              disabled={esRoot}
              style={esRoot ? { opacity: 0.4, cursor: 'not-allowed' } : undefined}
              onClick={() => !esRoot && setEliminarPendiente(u)}
            >
              🗑️
            </button>
          </div>
        );
      },
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 20 }}>
        <div>
          <div style={{ fontSize: 19, fontWeight: 700, color: 'var(--heading)' }}>Team</div>
          <div style={{ fontSize: 12.5, color: 'var(--muted)', marginTop: 3 }}>
            {usuarios ? `${usuarios.length} empleado${usuarios.length !== 1 ? 's' : ''} registrado${usuarios.length !== 1 ? 's' : ''}` : 'Cargando…'}
          </div>
        </div>
        {puedeEscribir && <Button onClick={() => navigate('/usuarios/nuevo')}>+ Nuevo Empleado</Button>}
      </div>

      <div className="filters-bar">
        <input
          className="filter-input filter-search"
          type="text"
          placeholder="Buscar por nombre o correo..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        {rolFiltro && (
          <button className="ms-chip" style={{ cursor: 'pointer' }} onClick={() => setRolFiltro('')}>
            Rol: {ROL_LABEL[rolFiltro] ?? rolFiltro} ✕
          </button>
        )}
      </div>

      <Table
        columns={columns}
        data={usuariosFiltrados}
        rowKey={u => u.id}
        onRowClick={u => navigate(`/usuarios/${u.id}`)}
        emptyMessage={isLoading ? 'Cargando…' : 'No hay usuarios que coincidan con los filtros'}
        footer={
          <span className="table-count">
            {usuariosFiltrados.length} usuario{usuariosFiltrados.length !== 1 ? 's' : ''}
          </span>
        }
      />

      <ConfirmModal
        open={desactivarPendiente !== null}
        title="¿Desactivar este usuario?"
        body={
          desactivarPendiente
            ? `Se desactivará la cuenta de "${desactivarPendiente.nombre}". Su sesión activa se cerrará de inmediato (se revoca su refresh token) y no podrá iniciar sesión hasta que se reactive.`
            : ''
        }
        confirmLabel="Sí, desactivar"
        danger
        onConfirm={confirmarDesactivar}
        onCancel={() => setDesactivarPendiente(null)}
      />

      <ConfirmModal
        open={eliminarPendiente !== null}
        title="¿Eliminar este usuario?"
        body={
          eliminarPendiente
            ? `Este usuario ("${eliminarPendiente.nombre}") dejará de aparecer en Team y no podrá iniciar sesión. Sus registros históricos (clientes, CxC, etc.) se conservarán intactos. Esta acción no puede deshacerse desde la interfaz.`
            : ''
        }
        confirmLabel="Sí, eliminar"
        danger
        onConfirm={confirmarEliminar}
        onCancel={() => setEliminarPendiente(null)}
      />

      <ToastStack toasts={toasts} onDismiss={dismiss} />
    </div>
  );
}
